import React, { useState } from 'react';
import { Box, ButtonBase, Dialog, IconButton, Paper, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CloseRounded as CloseIcon,
  FlipCameraIosOutlined as FlipCameraIcon,
  PhotoCameraOutlined as PhotoCameraIcon,
} from '@mui/icons-material';
import colors from '../../../assets/theme/colors';
import LogDateTimeFields from '../../UI/LogDateTimeFields';
import { DASHBOARD_QUICK_ENTRY_COPY } from './dashboardQuickEntryContent';
import { LOG_TYPES } from '../../../constants/logTypeRegistry';
import { getAdaptiveResponseChipLabel } from './adaptiveQuickLogConfig';
import VoiceAttachmentPreview from '../../../features/voiceCapture/VoiceAttachmentPreview';
import ImageCaptureIntentSelector from '../../../features/imageCapture/ImageCaptureIntentSelector';
import {
  CaptureComposerActionRow,
  CaptureSourceChoices,
  CaptureTagSuggestions,
} from './CaptureComposerControls';

const STRUCTURED_LOG_CHIPS = [
  { id: 'meals', label: 'Meals', logType: LOG_TYPES.food, handler: 'onOpenFoodLog' },
  { id: 'meds', label: 'Meds', logType: LOG_TYPES.medication, handler: 'onOpenMedicalLog' },
  { id: 'bathroom', label: 'Bathroom', logType: LOG_TYPES.bathroom, handler: 'onOpenBathroomLog' },
  { id: 'response', label: 'Response', logType: LOG_TYPES.behavior, handler: 'onOpenIncident' },
];

const CareNoteComposerSurface = ({
  child,
  dataCy,
  flush = false,
  sx = {},
  contentSx = {},
  captureDate,
  onCaptureDateChange,
  composerInputRef,
  composerText,
  onComposerFocus,
  onComposerTextChange,
  composerSaving = false,
  composerSavingLabel = 'Saving...',
  composerSavingHelper = 'Saving your note and organizing care details...',
  composerError = '',
  hasComposerContent = false,
  onContinue,
  onDismiss,
  onOpenSleepLog,
  onOpenFoodLog,
  onOpenMedicalLog,
  onOpenBathroomLog,
  onOpenIncident,
  onTakePhoto,
  onUploadImage,
  onUploadFile,
  onRecordVoice,
  onForwardEmail,
  composerAudioBlob,
  composerAudioFileName,
  onRemoveVoice,
  photoInputRef,
  onPhotoSelect,
  composerPhotoFile,
  composerPhotoDisplayName = '',
  composerPhotoPreviewUrl,
  onRemovePhoto,
  composerPhotoIntent,
  onComposerPhotoIntentChange,
  cameraOpen = false,
  cameraError = '',
  onCloseCamera,
  onFlipCamera,
  videoRef,
  onCapturePhoto,
  canvasRef,
}) => {
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const profileLabel = child?.name || '';
  const profileInitial = profileLabel.trim().charAt(0).toUpperCase();
  const Surface = flush ? Box : Paper;
  const surfaceProps = flush ? {} : { elevation: 0 };
  const structuredLogHandlers = {
    onOpenSleepLog,
    onOpenFoodLog,
    onOpenMedicalLog,
    onOpenBathroomLog,
    onOpenIncident,
  };
  const visibleStructuredChips = STRUCTURED_LOG_CHIPS.filter((chip) => (
    typeof structuredLogHandlers[chip.handler] === 'function'
  )).map((chip) => (
    chip.id === 'response'
      ? { ...chip, label: getAdaptiveResponseChipLabel(child) }
      : chip
  ));
  const logFieldSx = {
    minHeight: { xs: 64, sm: 68 },
    borderRadius: '16px',
    border: `1.5px solid ${alpha(colors.semantic.line2, 0.86)}`,
    bgcolor: colors.semantic.sageMist,
    px: { xs: 1.25, sm: 1.45 },
    py: 1.05,
    '&:hover': {
      borderColor: alpha(colors.semantic.profileFlow.completionText, 0.48),
      transform: 'none',
    },
    '& svg': {
      color: colors.semantic.profileFlow.completionText,
      fontSize: 20,
    },
    '& .MuiTypography-root:first-of-type': {
      fontSize: '0.68rem',
      letterSpacing: '0.11em',
      fontWeight: 800,
      color: alpha(colors.semantic.ink, 0.45),
      mb: 0.35,
    },
    '& .MuiTypography-root:last-of-type': {
      fontSize: { xs: '0.94rem', sm: '1rem' },
      fontWeight: 800,
      color: colors.semantic.ink,
      lineHeight: 1.08,
    },
  };

  return (
    <Surface
      {...surfaceProps}
      data-cy={dataCy}
      aria-label={DASHBOARD_QUICK_ENTRY_COPY.ariaLabel}
      title={DASHBOARD_QUICK_ENTRY_COPY.title}
      sx={{
        position: 'relative',
        overflow: flush ? 'visible' : 'hidden',
        ...(flush
          ? {
              borderRadius: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              border: 'none',
              boxShadow: 'none',
            }
          : {
              borderRadius: { xs: '26px', sm: '28px' },
              border: `1.5px solid ${alpha(colors.semantic.line2, 0.78)}`,
              boxShadow: `0 18px 42px ${alpha(colors.semantic.ink, 0.14)}`,
            }),
        bgcolor: colors.semantic.bg,
        px: { xs: 1.5, sm: 1.85 },
        pt: { xs: 1.1, sm: 1.25 },
        pb: { xs: 1.45, sm: 1.65 },
        ...(flush
          ? {
              flex: 'none',
            }
          : {}),
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          ...contentSx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.65 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: '1.22rem', sm: '1.36rem' },
                fontWeight: 950,
                lineHeight: 1.05,
                color: colors.semantic.ink,
                letterSpacing: 0,
              }}
            >
              Log something new
            </Typography>

            {profileLabel ? (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.55,
                  minWidth: 0,
                  maxWidth: '100%',
                  px: 0.7,
                  py: 0.42,
                  borderRadius: '999px',
                  bgcolor: alpha(colors.semantic.profileFlow.completionBg, 0.86),
                  color: colors.semantic.profileFlow.completionText,
                }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '999px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: colors.semantic.profileFlow.completionText,
                    color: colors.semantic.bg,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {profileInitial}
                </Box>
                <Typography
                  sx={{
                    maxWidth: 170,
                    fontSize: '0.82rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: colors.semantic.profileFlow.completionText,
                  }}
                >
                  {profileLabel}
                </Typography>
              </Box>
            ) : null}
          </Box>
          <IconButton
            onClick={onDismiss}
            disabled={composerSaving}
            aria-label="Close log update"
            title="Close"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              mt: { xs: -0.3, sm: -0.15 },
              bgcolor: alpha(colors.semantic.ink, 0.06),
              color: alpha(colors.semantic.ink, 0.62),
              '&:hover': {
                bgcolor: alpha(colors.semantic.ink, 0.1),
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>

        <LogDateTimeFields
          value={captureDate}
          onChange={onCaptureDateChange}
          sx={{ mb: 1.25 }}
          dateFieldSx={logFieldSx}
          timeFieldSx={logFieldSx}
        />

        <CaptureSourceChoices
          open={sourcesOpen}
          composerSaving={composerSaving}
          onTakePhoto={onTakePhoto}
          onUploadImage={onUploadImage}
          onUploadFile={onUploadFile}
          onForwardEmail={onForwardEmail}
        />

        <CaptureTagSuggestions
          open={tagsOpen}
          chips={visibleStructuredChips}
          handlers={structuredLogHandlers}
          child={child}
          onDismiss={onDismiss}
          composerSaving={composerSaving}
        />

        <TextField
          inputRef={composerInputRef}
          value={composerText}
          onFocus={onComposerFocus}
          onChange={onComposerTextChange}
          placeholder="What changed, what helped, or what should others know?"
          aria-label="What happened"
          multiline
          minRows={3}
          maxRows={7}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: { xs: 128, sm: 144 },
              alignItems: 'flex-start',
              borderRadius: '22px',
              bgcolor: colors.semantic.bg,
              color: colors.semantic.ink,
              fontSize: { xs: '0.96rem', sm: '1rem' },
              lineHeight: 1.42,
              fontWeight: 400,
              px: { xs: 1.45, sm: 1.65 },
              py: { xs: 1.4, sm: 1.55 },
              '& fieldset': {
                borderColor: alpha(colors.semantic.profileFlow.completionText, 0.26),
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: alpha(colors.semantic.profileFlow.completionText, 0.44),
              },
              '&.Mui-focused fieldset': {
                borderColor: alpha(colors.semantic.profileFlow.completionText, 0.74),
                borderWidth: '1.5px',
              },
              '& textarea': {
                p: 0,
                fontWeight: 400,
                '&::placeholder': {
                  color: alpha(colors.semantic.profileFlow.completionText, 0.62),
                  opacity: 1,
                  fontWeight: 400,
                },
              },
            },
          }}
        />

        <input ref={photoInputRef} type="file" accept="image/*" hidden onChange={onPhotoSelect} />

        {composerPhotoPreviewUrl ? (
          <>
          <Box sx={{ mt: 0.95, display: 'grid', gridTemplateColumns: '58px minmax(0, 1fr) auto', alignItems: 'center', gap: 0.8, p: 0.65, borderRadius: '16px', bgcolor: alpha(colors.brand.tint, 0.08), border: `1px solid ${alpha(colors.brand.tint, 0.18)}` }}>
            <Box component="img" src={composerPhotoPreviewUrl} alt={composerPhotoFile?.name || 'Selected photo preview'} sx={{ width: 58, height: 46, objectFit: 'cover', borderRadius: '12px', border: `1px solid ${alpha(colors.semantic.line, 0.7)}`, bgcolor: colors.semantic.bg }} />
            <Typography sx={{ minWidth: 0, fontSize: '0.76rem', fontWeight: 600, color: colors.semantic.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {composerPhotoDisplayName || composerPhotoFile?.name || 'Photo attached'}
            </Typography>
            <IconButton size="small" onClick={onRemovePhoto} aria-label="Remove attached photo" sx={{ width: 30, height: 30, color: alpha(colors.semantic.ink, 0.68) }}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>
          <ImageCaptureIntentSelector value={composerPhotoIntent} onChange={onComposerPhotoIntentChange} />
          </>
        ) : null}

        <VoiceAttachmentPreview audioBlob={composerAudioBlob} fileName={composerAudioFileName} onRemove={onRemoveVoice} />

        <CaptureComposerActionRow
          sourcesOpen={sourcesOpen}
          onToggleSources={() => setSourcesOpen((value) => !value)}
          tagsOpen={tagsOpen}
          onToggleTags={() => setTagsOpen((value) => !value)}
          showTags={visibleStructuredChips.length > 0}
          onRecordVoice={onRecordVoice}
          onContinue={onContinue}
          hasComposerContent={hasComposerContent}
          composerSaving={composerSaving}
          composerSavingLabel={composerSavingLabel}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.05, mt: 0.9 }}>
          {composerError ? (
            <Box role="alert" sx={{ px: 1.5, py: 1.1, borderRadius: '14px', bgcolor: alpha(colors.semantic.error, 0.08), color: colors.semantic.error }}>
              <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.45, fontWeight: 700, color: 'inherit' }}>
                {composerError}
              </Typography>
            </Box>
          ) : null}
          {!composerSaving ? (
            <Typography sx={{ px: 0.5, fontSize: '0.76rem', lineHeight: 1.4, color: colors.semantic.ink2 }}>
              Photo, voice, and note send together.
            </Typography>
          ) : null}
          {composerSaving ? (
            <Typography role="status" aria-live="polite" sx={{ px: 2, fontSize: '0.82rem', lineHeight: 1.45, textAlign: 'center', color: colors.semantic.ink2 }}>
              {composerSavingHelper}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Dialog open={cameraOpen || Boolean(cameraError)} onClose={onCloseCamera} fullWidth maxWidth="xs" PaperProps={{ sx: { m: 1.5, borderRadius: '22px', overflow: 'hidden' } }}>
        <Box sx={{ position: 'relative', bgcolor: cameraError ? colors.semantic.bg : colors.landing.deepNavy }}>
          <IconButton onClick={onCloseCamera} aria-label="Close camera" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: alpha(colors.landing.deepNavy, cameraError ? 0.08 : 0.72), color: cameraError ? colors.semantic.ink : colors.semantic.bg }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          {cameraError ? (
            <Box sx={{ px: 2.2, py: 3.2, pr: 6 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: colors.semantic.ink, mb: 0.5 }}>
                Camera unavailable
              </Typography>
              <Typography sx={{ fontSize: '0.86rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.68) }}>
                {cameraError}
              </Typography>
            </Box>
          ) : (
            <>
              <IconButton onClick={onFlipCamera} aria-label="Flip camera" sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, bgcolor: alpha(colors.landing.deepNavy, 0.72), color: colors.semantic.bg }}>
                <FlipCameraIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Box component="video" ref={videoRef} autoPlay playsInline muted sx={{ width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover', backgroundColor: colors.landing.deepNavy }} />
              <Box sx={{ p: 1.25, bgcolor: colors.semantic.bg, display: 'flex', justifyContent: 'center' }}>
                <ButtonBase aria-label="Capture photo" onClick={onCapturePhoto} sx={{ minHeight: 44, px: 2.4, borderRadius: '999px', bgcolor: colors.brand.deep, color: colors.semantic.bg, display: 'inline-flex', alignItems: 'center', gap: 0.65, fontWeight: 900 }}>
                  <PhotoCameraIcon sx={{ fontSize: 18, color: 'inherit' }} />
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, lineHeight: 1 }}>
                    Capture
                  </Typography>
                </ButtonBase>
              </Box>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          )}
        </Box>
      </Dialog>
    </Surface>
  );
};

export default CareNoteComposerSurface;
